import { getFlexNode } from '@play-co/timestep-core/flexbox';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import { assertNever } from 'src/replicant/utils';
import { ActionSequence } from 'src/lib/ActionSequence';
import { socialSequences } from '../social/social.sequence';
import LockedButtonOverlay from './LockedButtonOverlay';
import sounds from 'src/lib/sounds';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { startVisitSequence } from '../visit/visit.sequence';
import StateObserver from '../../StateObserver';
import { Visit } from 'src/replicant/visit';
import statePromise from '../../lib/statePromise';
import { Feature } from 'src/replicant/analytics';
import { openDuoPopup } from '../duo/duo.sequence';
import animate from '@play-co/timestep-core/lib/animate';
import { duration } from 'src/replicant/utils/duration';
import { isPlatformGuest } from 'src/lib/utils';
import platform from '@play-co/gcinstant';

const ui = uiConfig.buruburu;

export type FriendsAction =
  | 'invite'
  | 'gift'
  | 'visitorList'
  | 'visitFriend'
  | 'duoReceivedList'
  | 'duoSentList';

type FriendsActionButtonOpts = {
  type: FriendsAction;
  onSelect: () => void;
  closeMenu: () => void;
  isLocked?: boolean;
  lockedReason?: 'sleeping' | 'visitingFriend' | 'dead' | 'visitFriend';
};

export default class FriendsActionButton extends ButtonScaleViewWithText {
  private isLocked: boolean;
  private lockedReason?: 'sleeping' | 'visitingFriend' | 'dead' | 'visitFriend';
  private lockedButtonOverlay;

  constructor(private opts: FriendsActionButtonOpts) {
    super({
      ...ui.menu.menuButton,
      icon: ui.friends.actionIcons[opts.type],
      localeText: () => i18n(`friendsAction.${opts.type}`),
      tracking: { feature: 'friendsMenu', $subFeature: opts.type },
    });

    this.icon.updateOpts({ scaleMethod: 'contain' });

    if (opts.isLocked) {
      this.setLocked(true, opts.lockedReason);
    }
  }

  setLocked(
    isLocked: boolean,
    reason?: 'sleeping' | 'visitingFriend' | 'dead' | 'visitFriend',
  ) {
    this.lockedReason = reason;
    if (this.isLocked === isLocked) {
      return;
    }

    this.isLocked = isLocked;
    if (isLocked) {
      const { width, height } = this.style;
      this.lockedButtonOverlay = this.addSubview(
        new LockedButtonOverlay({ width, height }),
      );
    } else if (this.lockedButtonOverlay) {
      const overlay = this.lockedButtonOverlay;
      animate(overlay)
        .commit()
        .now({ opacity: 0 }, 250, animate.linear)
        .then(() => overlay.removeFromSuperview());
      this.lockedButtonOverlay = null;
    }
  }

  public async onClick() {
    this.opts.onSelect();

    if (this.isLocked) {
      await openPopupPromise('popupFeatureDisabled', {
        feature: this.opts.type,
        reason: this.lockedReason,
      });

      sounds.sfx.play('cancel');

      return;
    }

    if (isPlatformGuest(platform)) {
      await openPopupPromise('popupNativeLineGuestLogin', {
        origin: 'feature',
      });
      sounds.sfx.play('cancel');
      return;
    }

    switch (this.opts.type) {
      case 'invite': {
        await ActionSequence.start(
          socialSequences.invite(Feature.INVITE.INVITE_ICON),
          'Invite',
        );
        break;
      }
      case 'gift': {
        await openPopupPromise('popupGiftsList', {});
        break;
      }
      case 'visitorList':
      case 'visitFriend': {
        // Wait until visit session starts or user cancels the sequence
        await Promise.race([
          startVisitSequence(
            this.opts.type === 'visitorList' ? 'visitors' : 'friends',
          ),
          statePromise(({ user }) => Visit.getters(user).isVisiting()),
        ]);

        if (Visit.getters(StateObserver.getState().user).isVisiting()) {
          this.opts.closeMenu();
        }

        break;
      }

      case 'duoReceivedList':
      case 'duoSentList': {
        const closeMenu = await openDuoPopup(
          this.opts.type === 'duoReceivedList' ? 'received' : 'sent',
        );

        if (closeMenu) {
          this.opts.closeMenu();
        }

        break;
      }

      default:
        assertNever(this.opts.type);
    }
  }
}
